import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { DAY_TYPES } from '~/lib/schemas/available-date';

/** The day-shift fields shared by the work-day and exception-day editors. */
export interface ShiftValues {
	dayType: (typeof DAY_TYPES)[number];
	startTime: string | null;
	endTime: string | null;
	startTime2: string | null;
	endTime2: string | null;
	cutOffTime: string | null;
	fc: number | null;
}

/** Trim an API time ("HH:mm:ss") to the "HH:mm" an `<input type=time>` expects. */
const toTimeInput = (v: string | null) => (v ? v.slice(0, 5) : '');

export function ShiftFields({ value, onChange }: { value: ShiftValues; onChange: (patch: Partial<ShiftValues>) => void }) {
	const { t } = useTranslation();
	const time = (key: keyof ShiftValues, label: string) => (
		<FormField label={label}>
			<Input
				type="time"
				value={toTimeInput(value[key] as string | null)}
				onChange={e => onChange({ [key]: e.target.value || null } as Partial<ShiftValues>)}
			/>
		</FormField>
	);

	return (
		<div className="flex flex-col gap-3">
			<FormField label={t('system.availableDates.columns.dayType')}>
				<Select value={value.dayType} onValueChange={v => onChange({ dayType: v as ShiftValues['dayType'] })}>
					<SelectTrigger className="w-full" aria-label={t('system.availableDates.columns.dayType')}>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{DAY_TYPES.map(dt => (
							<SelectItem key={dt} value={dt}>
								{t(`system.availableDates.dayTypes.${dt}`)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</FormField>
			<div className="grid grid-cols-2 gap-3">
				{time('startTime', t('system.availableDates.startTime'))}
				{time('endTime', t('system.availableDates.endTime'))}
				{time('startTime2', t('system.availableDates.startTime2'))}
				{time('endTime2', t('system.availableDates.endTime2'))}
			</div>
			<div className="grid grid-cols-2 gap-3">
				{time('cutOffTime', t('system.availableDates.cutOffTime'))}
				<FormField label={t('system.availableDates.fc')}>
					<Input
						type="number"
						value={value.fc ?? ''}
						onChange={e => onChange({ fc: e.target.value === '' ? null : Number(e.target.value) })}
					/>
				</FormField>
			</div>
		</div>
	);
}
